const NON_NARROWING = new Set(['not_sure', 'cannot_observe', 'not_present', 'not_applicable', 'deferred', 'variable'])

export function isNonNarrowing(observation) {
  return !observation || NON_NARROWING.has(observation.kind) || observation.certainty === 'tentative'
}

function profileMatches(profileValue, observation) {
  if (isNonNarrowing(observation)) return true
  if (observation.kind === 'numeric') {
    if (!profileValue || typeof profileValue !== 'object') return true
    const value = Number(observation.value)
    return value >= profileValue.min && value <= profileValue.max
  }
  return profileValue === observation.value
}

export function getCompatibleProfiles(profiles, observations) {
  return profiles.filter((profile) => Object.entries(observations).every(([questionId, observation]) => {
    if (observation.certainty === 'fairly_sure') return true
    return profileMatches(profile.values[questionId], observation)
  }))
}

export function scoreProfiles(profiles, observations) {
  return profiles.map((profile) => {
    let score = 0
    Object.entries(observations).forEach(([questionId, observation]) => {
      if (isNonNarrowing(observation)) return
      const matches = profileMatches(profile.values[questionId], { ...observation, certainty: 'certain' })
      const weight = observation.certainty === 'fairly_sure' ? .45 : 1
      score += matches ? weight : -weight
    })
    return { ...profile, score }
  }).sort((a, b) => b.score - a.score || a.id.localeCompare(b.id))
}

export function isQuestionObservable(question, context, experience, observations) {
  if (observations[question.id]) return false
  if (question.condition) {
    const prior = observations[question.condition.questionId]
    if (prior?.kind !== 'state' || prior.value !== question.condition.stateId) return false
  }
  if (context.allowOverrides) return true
  if (question.material?.length && !question.material.some((material) => context.materials.includes(material))) return false
  if (question.tool && !context[question.tool]) return false
  if (question.months && !question.months.includes(Number(context.month))) return false
  if (experience === 'beginner' && question.difficulty === 'advanced' && question.tool === 'handLens' && !context.handLens) return false
  return true
}

function modeWeight(question, experience) {
  if (experience === 'beginner') return question.difficulty === 'easy' ? .45 : question.difficulty === 'advanced' ? -.55 : 0
  if (experience === 'advanced') return question.responseType === 'numeric' ? .65 : question.difficulty === 'advanced' ? .35 : -.08
  return question.difficulty === 'intermediate' ? .2 : 0
}

function toolWeight(question, context) {
  if (!question.tool) return 0
  return context[question.tool] ? .28 : -.6
}

export function scoreQuestion(question, context, experience, remainingCount) {
  const narrowingNeed = remainingCount > 1 ? Math.min(.35, (remainingCount - 1) * .07) : -.15
  return question.demoGain + modeWeight(question, experience) + toolWeight(question, context) + narrowingNeed
}

export function selectNextQuestion({ questions, profiles, observations, context, experience }) {
  const compatible = getCompatibleProfiles(profiles, observations)
  const poolSize = compatible.length || profiles.length
  const ranked = questions
    .filter((question) => isQuestionObservable(question, context, experience, observations))
    .map((question) => ({ question, score: scoreQuestion(question, context, experience, poolSize) }))
    .sort((a, b) => b.score - a.score || a.question.id.localeCompare(b.question.id))
  if (!ranked.length) return null
  const selected = ranked[0]
  return {
    ...selected,
    reason: `${selected.question.why} ${poolSize} anonymous profile${poolSize === 1 ? '' : 's'} currently remain in this limited demonstration.`,
  }
}

export function evaluateObservation({ profiles, observations, questionId, observation }) {
  const before = getCompatibleProfiles(profiles, observations)
  const nextObservations = { ...observations, [questionId]: observation }
  const after = getCompatibleProfiles(profiles, nextObservations)
  const contradiction = after.length === 0 && Object.values(nextObservations).some((item) => !isNonNarrowing(item))
  let message = 'Observation recorded.'
  if (isNonNarrowing(observation)) message = 'Observation retained without eliminating anonymous profiles.'
  else if (observation.certainty === 'fairly_sure') message = 'Your fairly sure observation affects profile ranking without hard elimination.'
  else if (after.length < before.length) message = `Anonymous profiles reduced from ${before.length} to ${after.length}.`
  if (contradiction) message = 'This combination is inconsistent with the limited demonstration profiles. Your observations have been retained.'
  return { before, after, nextObservations, contradiction, message }
}

export function findConflictingObservationIds(profiles, observations) {
  if (getCompatibleProfiles(profiles, observations).length) return []
  return Object.keys(observations).filter((questionId) => {
    const reduced = { ...observations }
    delete reduced[questionId]
    return getCompatibleProfiles(profiles, reduced).length > 0
  })
}

export function createInitialContext(month = new Date().getMonth() + 1) {
  return { month, materials: [], distance: 'in_hand', handLens: false, ruler: false, allowOverrides: false }
}

